/**
 * Sysadmin Tenant Detail — parent layout (Wave 3 §10.2 #2).
 *
 * Per docs/SYSADMIN-PLATFORM-SPEC.md §4: 9 route-driven tabs replacing
 * the prior `useState` 6-tab implementation. Each tab is a real route
 * so refresh + deep-link work. Tabs:
 *
 *   /sysadmin/tenants/:id              — Overview (index child)
 *   /sysadmin/tenants/:id/settings     — Identity / mode
 *   /sysadmin/tenants/:id/assignments  — Asset assignments (Models + MCP) ⭐
 *   /sysadmin/tenants/:id/compliance   — Compliance profile (read-only v1)
 *   /sysadmin/tenants/:id/limits       — Resource limits (storage / tokens / GPU)
 *   /sysadmin/tenants/:id/branding     — Branding override + lock matrix
 *   /sysadmin/tenants/:id/users        — Tenant user roster (read-only)
 *   /sysadmin/tenants/:id/audit        — Tenant-scoped audit
 *
 * Layout responsibilities:
 *   - Sticky header (name / slug / status / lifecycle action buttons)
 *   - Sticky tab bar with active-tab indicator
 *   - Suspend / Delete / Restore dialogs (shared across tabs)
 *   - <Outlet /> for child tab content
 *
 * Data:
 *   useTenantDetail() lives here AND in each child via TanStack Query
 *   shared cache (queryKey ['platform-tenant', tenantId]). No props
 *   drilling — children re-call the hook and TanStack returns the
 *   cached payload synchronously.
 */

import { useState } from 'react';
import {
  createFileRoute, Link, Outlet, useParams, useLocation, useNavigate,
} from '@tanstack/react-router';
import {
  Building2, ArrowLeft, AlertTriangle, Trash2, PauseCircle, PlayCircle,
  Settings as SettingsIcon, Users, Database, ScrollText, BarChart3,
  RotateCcw, Network, ShieldAlert, Gauge, Palette, Brain, Cpu,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useTenantDetail,
  useSuspendTenant,
  useRestoreTenant,
  useDeleteTenant,
  useRestoreDeletedTenant,
  type TenantStatus,
} from '@/lib/use-tenants';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId')({
  component: TenantDetailLayout,
});

const TABS = [
  { key: 'overview',    label: 'Overview',        icon: BarChart3,   path: '' },
  { key: 'settings',    label: 'Settings',        icon: SettingsIcon, path: 'settings' },
  { key: 'assignments', label: 'Assignments',     icon: Network,     path: 'assignments' },
  { key: 'hardware',    label: 'Hardware',        icon: Cpu,         path: 'hardware' },
  { key: 'compliance',  label: 'Compliance',      icon: ShieldAlert, path: 'compliance' },
  { key: 'limits',      label: 'Resource Limits', icon: Gauge,       path: 'limits' },
  { key: 'branding',    label: 'Branding',        icon: Palette,     path: 'branding' },
  { key: 'memory',      label: 'Memory',          icon: Brain,       path: 'memory' },
  { key: 'users',       label: 'Users',           icon: Users,       path: 'users' },
  { key: 'audit',       label: 'Audit',           icon: ScrollText,  path: 'audit' },
] as const;

function TenantDetailLayout() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId' });
  const { data, isLoading, error } = useTenantDetail(tenantId);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-muted-foreground animate-pulse">Loading tenant…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <Link
          to="/sysadmin/tenants"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to tenants
        </Link>
        <Card>
          <CardContent className="py-10 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h2 className="font-semibold">Tenant not found</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {error?.message || `No tenant with id ${tenantId}`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { tenant } = data;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      {/* Sticky header (per spec §4) */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <Link
            to="/sysadmin/tenants"
            className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            All tenants
          </Link>
          <div className="flex items-center gap-3">
            <Building2 className="h-7 w-7 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                {tenant.name}
                <StatusBadge status={tenant.status} />
                {tenant.isRoot && (
                  <Badge variant="outline" className="text-xs">Root</Badge>
                )}
              </h1>
              <div className="text-xs text-muted-foreground font-mono mt-1">
                {tenant.slug} · {tenant.orgCode} · {tenant.id}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LifecycleButtons
            tenant={tenant}
            onSuspend={() => setSuspendOpen(true)}
            onDelete={() => setDeleteOpen(true)}
          />
        </div>
      </div>

      {/* Sticky tab bar — every tab is a real route */}
      <TabBar tenantId={tenantId} />

      {/* Child tab content */}
      <Outlet />

      {/* W1a-20 lifecycle dialogs */}
      <SuspendDialog
        open={suspendOpen}
        onOpenChange={setSuspendOpen}
        tenant={tenant}
      />
      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        tenant={tenant}
      />
    </div>
  );
}

// ─── Tab bar (route-driven) ─────────────────────────────────────────────────

function TabBar({ tenantId }: { tenantId: string }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Strip the tenant prefix and a single '/' so we get the tab segment
  // ('' for the index/Overview, 'settings', 'features', …).
  const base = `/sysadmin/tenants/${tenantId}`;
  const rawTail = location.pathname.startsWith(base)
    ? location.pathname.slice(base.length)
    : '';
  const tail = rawTail.replace(/^\/+/, '').split('/')[0] ?? '';

  return (
    <div className="border-b sticky top-0 bg-background z-10">
      <nav className="flex items-center gap-0.5 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tail === t.path;
          const to = t.path === ''
            ? `/sysadmin/tenants/${tenantId}`
            : `/sysadmin/tenants/${tenantId}/${t.path}`;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => navigate({ to })}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                active
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

// ─── Status badge (shared) ──────────────────────────────────────────────────

function StatusBadge({ status }: { status: TenantStatus }) {
  const config = {
    active:    { label: 'Active',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    pending:   { label: 'Pending',   cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    suspended: { label: 'Suspended', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    deleted:   { label: 'Deleted',   cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
  }[status];
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', config.cls)}>
      {config.label}
    </span>
  );
}

// ─── Lifecycle buttons ──────────────────────────────────────────────────────

function LifecycleButtons({ tenant, onSuspend, onDelete }: {
  tenant: { status: TenantStatus; id: string; slug: string };
  onSuspend: () => void;
  onDelete: () => void;
}) {
  const restore = useRestoreTenant();
  const restoreDeleted = useRestoreDeletedTenant();

  if (tenant.status === 'deleted') {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={restoreDeleted.isPending}
        onClick={() => restoreDeleted.mutate({ id: tenant.id })}
      >
        <RotateCcw className="h-4 w-4 mr-2" />
        {restoreDeleted.isPending ? 'Restoring…' : 'Restore from delete'}
      </Button>
    );
  }

  if (tenant.status === 'suspended') {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          disabled={restore.isPending}
          onClick={() => restore.mutate({ id: tenant.id })}
        >
          <PlayCircle className="h-4 w-4 mr-2" />
          {restore.isPending ? 'Restoring…' : 'Restore'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          className="text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
      </>
    );
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={onSuspend}>
        <PauseCircle className="h-4 w-4 mr-2" />
        Suspend
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onDelete}
        className="text-destructive hover:bg-destructive/10"
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete
      </Button>
    </>
  );
}

// ─── Suspend dialog ─────────────────────────────────────────────────────────

function SuspendDialog({ open, onOpenChange, tenant }: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  tenant: { id: string; name: string; slug: string };
}) {
  const suspend = useSuspendTenant();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!reason.trim()) {
      setError('A reason is required so the audit trail captures why the tenant was suspended.');
      return;
    }
    try {
      await suspend.mutateAsync({ id: tenant.id, reason: reason.trim() });
      setReason('');
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Suspend failed');
    }
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setReason('');
      setError(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Suspend tenant</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{tenant.name}</span>'s
            users will not be able to log in until the tenant is restored.
            Existing sessions are not revoked automatically — they expire
            naturally on token refresh.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded p-3 text-sm flex gap-2 items-start">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              The reason is recorded in <code>platform_audit_logs</code> and
              shown in the support ticket trail. Be specific.
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="suspend-reason">Reason</Label>
            <Textarea
              id="suspend-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Customer requested temporary pause / overdue invoice / pending compliance review…"
              rows={3}
            />
          </div>

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={suspend.isPending}>
            {suspend.isPending ? 'Suspending…' : 'Suspend tenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete dialog ──────────────────────────────────────────────────────────

function DeleteDialog({ open, onOpenChange, tenant }: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  tenant: { id: string; name: string; slug: string };
}) {
  const del = useDeleteTenant();
  const [confirmSlug, setConfirmSlug] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      await del.mutateAsync({ id: tenant.id, confirmSlug: confirmSlug.trim() });
      setConfirmSlug('');
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setConfirmSlug('');
      setError(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete tenant</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{tenant.name}</span> will
            be soft-deleted. You have <strong>60 days</strong> to restore it
            via the "Restore from delete" action; after that, the tenant is
            eligible for permanent purge.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm flex gap-2 items-start">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div>
              Type the tenant slug <code className="bg-background px-1 rounded">{tenant.slug}</code> below
              to confirm. Tenant users will be locked out immediately.
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="delete-confirm">Confirm slug</Label>
            <Input
              id="delete-confirm"
              value={confirmSlug}
              onChange={(e) => setConfirmSlug(e.target.value)}
              placeholder={tenant.slug}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          </div>

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={del.isPending || confirmSlug.trim() !== tenant.slug}
          >
            {del.isPending ? 'Deleting…' : 'Soft-delete tenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
